// FILE: /src/store/Slices/GroupSlice.jsx (FULLY UPDATED)

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosServices from "../../services/axios";
import { setCalendarFilters } from "./CalendarFilterSlice";
import { fetchInstagramProfile, fetchfacebookProfile } from "../../services/group";
import { fetchAssets } from "./librarySlice";

export const handleAuthSuccess = createAsyncThunk(
"groups/handleAuthSuccess",
async (_, { dispatch, rejectWithValue }) => {
try {
await dispatch(fetchGroups()).unwrap();
await dispatch(fetchAllProfileDetails()).unwrap();

sessionStorage.removeItem("temp_selected_group_for_auth");
return { success: true };
} catch (error) {
console.error("Error during post-auth data refresh:", error);
sessionStorage.removeItem("temp_selected_group_for_auth");
return rejectWithValue(error.message || "Failed to refresh data after connection.");
}
}
);

export const fetchAllProfileDetails = createAsyncThunk(
"groups/fetchAllProfileDetails",
async (_, { getState, rejectWithValue }) => {
const { groups } = getState().groups;
if (!groups || groups.length === 0) return {};

const allProfilesResponse = await axiosServices.get('/profiles/get-profile');
const masterYouTubeProfile = allProfilesResponse.data.profiles.find(p => p.platform === 'YOUTUBE_MASTER');
const youtubeRefreshToken = masterYouTubeProfile?.refresh_token;

const allAccounts = groups.flatMap(group => group.connected_accounts || []);
const uniqueProfileIds = new Map();
allAccounts.forEach(acc => {
const key = acc.platform === 'FACEBOOK' ? acc.platform_pageId : acc.platform_id;
if (key && !uniqueProfileIds.has(key)) {
uniqueProfileIds.set(key, acc);
}
});

const detailPromises = Array.from(uniqueProfileIds.values()).map(async (account) => {
const key = account.platform === 'FACEBOOK' ? account.platform_pageId : account.platform_id;
try {
let details;
if (account.platform === 'INSTAGRAM') {
const data = await fetchInstagramProfile(account.platform_id);
details = { name: data.username, username: data.username, profile_picture_url: data.profile_picture_url };
} else if (account.platform === 'PINTEREST') {
const { data } = await axiosServices.post(`/pinterest/get-profile`, { user_id: account.platform_id });
details = data.userAccount;
} else if (account.platform === 'FACEBOOK') {
const data = await fetchfacebookProfile({ pageId: account.platform_pageId, user_id: account.platform_id });
details = { ...data, profile_picture_url: `https://graph.facebook.com/${account.platform_pageId}/picture?type=normal` };
}
else if (account.platform === 'LINKEDIN') {
const masterProfileResponse = await axiosServices.post('/profiles/get-platform-profile', { platform: 'LINKEDIN' });
const lnProfile = masterProfileResponse.data.profiles.find(p => p.user_id === account.platform_id);

if (lnProfile && lnProfile.page_details && lnProfile.page_details.length > 0) {
details = {
name: lnProfile.page_details[0]?.profile,
username: lnProfile.page_details[0]?.profile,
profile_picture_url: lnProfile.page_details[0]?.profile_picture_url
};
} else {
details = { name: account.profile, username: account.profile, error: 'Details not found' };
}
}
else if (account.platform === 'GMB') {
details = { name: account.profile, username: account.profile, profile_picture_url: null };
}
else if (account.platform === 'YOUTUBE') {
if (!youtubeRefreshToken) {
throw new Error("YouTube master token not found on frontend. Please reconnect.");
}
const { data } = await axiosServices.post('/youtube/channel-details', {
channelId: account.platform_id,
refreshToken: youtubeRefreshToken
});
details = {
name: data.title,
username: data.title,
profile_picture_url: data.thumbnails?.default?.url
};
}
else if (account.platform === 'TIKTOK') {
const masterProfileResponse = await axiosServices.post('/profiles/get-platform-profile', { platform: 'TIKTOK' });
const tiktokProfile = masterProfileResponse.data.profiles.find(p => p.user_id === account.platform_id);

if (tiktokProfile && tiktokProfile.page_details && tiktokProfile.page_details.length > 0) {
details = {
name: tiktokProfile.page_details[0]?.profile,
username: tiktokProfile.page_details[0]?.profile,
profile_picture_url: tiktokProfile.page_details[0]?.profile_picture_url
};
} else {
details = { name: account.profile, username: account.profile, error: 'Details not found' };
}
}
return { key, details };
} catch (error) {
console.warn(`Could not fetch details for profile ${key}:`, error.message);
const errorType = error.response?.data?.errorType || null;
return { key, details: { name: 'Profile Error', error: errorType } };
}
});

const results = await Promise.all(detailPromises);
const profileDetailsMap = results.reduce((acc, { key, details }) => {
if (key && details) {
acc[key] = details;
}
return acc;
}, {});

return profileDetailsMap;
}
);

export const fetchGroups = createAsyncThunk(
"groups/fetchGroups",
async (_, { dispatch, rejectWithValue }) => {
try {
const response = await axiosServices.get("/group/get");
const fetchedGroups = response.data.groups || [];

// We no longer set the initial group here; App.js is responsible for that.
return fetchedGroups;
} catch (error) {
return rejectWithValue(error.response?.data || "An error occurred");
}
}
);

export const createGroup = createAsyncThunk(
"groups/createGroup",
async (payload, { rejectWithValue }) => {
try {
const response = await axiosServices.post("/group/create", payload);
if (response.data.success) {
return response.data;
} else {
return rejectWithValue(response.data);
}
} catch (error) {
return rejectWithValue(error.response?.data || "An error occurred");
}
}
);

export const deleteGroupDetails = createAsyncThunk(
"groups/deleteGroupDetails",
async (platform_id, { rejectWithValue }) => {
try {
const response = await axiosServices.post("/group/delete-profile", { platform_id });
return response.data;
} catch (error) {
console.error("Error deleting group details:", error);
return rejectWithValue(error.response?.data || "An error occurred");
}
}
);

export const fetchGroupDetails = createAsyncThunk(
"groups/fetchGroupDetails",
async (groupName, { dispatch, rejectWithValue }) => {
if (!groupName) {
return rejectWithValue("Group name is required to fetch details.");
}
try {
const response = await axiosServices.post("/group/get-particular", { groupName });

const accounts = response.data.group.connected_accounts || [];

const uniqueProfiles = accounts;

const allProfileIds = uniqueProfiles.map(p => p.platform === 'FACEBOOK' ? p.platform_pageId : p.platform_id).filter(Boolean);

dispatch(setCalendarFilters({ profiles: allProfileIds }));

return uniqueProfiles;
} catch (error) {
return rejectWithValue(error.response?.data || "An error occurred");
}
}
);

const groupSlice = createSlice({
name: "groups",
initialState: {
groups: [],
loading: false,
selectedGroup: localStorage.getItem("selected_group") || null,
profile: [],
profileDetails: {},
error: null,
},
reducers: {
_setSelectedGroup: (state, action) => {
const groupName = action.payload;
state.selectedGroup = groupName;
if (groupName) {
localStorage.setItem("selected_group", groupName);
} else {
localStorage.removeItem("selected_group");
}
},
clearGroups: (state) => {
state.groups = [];
state.error = null;
},
setProfiles: (state, action) => {
state.profile = action.payload;
},
updateGroupAndProfiles: (state, action) => {
const updatedGroup = action.payload;
if (!updatedGroup || !updatedGroup._id) return;
const groupIndex = state.groups.findIndex(g => g._id === updatedGroup._id);
if (groupIndex !== -1) {
state.groups[groupIndex] = updatedGroup;
}
if (state.selectedGroup === updatedGroup.name) {
state.profile = updatedGroup.connected_accounts;
}
},
},
extraReducers: (builder) => {
builder
.addCase(fetchGroups.pending, (state) => {
state.loading = true;
state.error = null;
})
.addCase(fetchGroups.fulfilled, (state, action) => {
state.loading = false;
state.groups = action.payload;
})
.addCase(fetchGroups.rejected, (state, action) => {
state.loading = false;
state.error = action.payload || "Failed to fetch groups";
})
.addCase(createGroup.fulfilled, (state, action) => {
if (action.payload.success) {
const updatedGroup = action.payload.group;
const groupIndex = state.groups.findIndex(g => g._id === updatedGroup._id);

if (groupIndex !== -1) {
state.groups[groupIndex] = updatedGroup;
} else {
state.groups.push(updatedGroup);
}
if (state.selectedGroup === updatedGroup.name) {
state.profile = updatedGroup.connected_accounts;
}
}
})
.addCase(fetchGroupDetails.pending, (state) => {
state.loading = true;
})
.addCase(fetchGroupDetails.fulfilled, (state, action) => {
state.loading = false;
state.profile = action.payload;
})
.addCase(fetchGroupDetails.rejected, (state, action) => {
state.loading = false;
state.profile = [];
state.error = action.payload;
})
.addCase(deleteGroupDetails.fulfilled, (state, action) => {})
.addCase(fetchAllProfileDetails.pending, (state) => {
state.loading = true;
})
.addCase(fetchAllProfileDetails.fulfilled, (state, action) => {
state.loading = false;
state.profileDetails = { ...state.profileDetails, ...action.payload };
})
.addCase(fetchAllProfileDetails.rejected, (state, action) => {
state.loading = false;
state.error = action.payload || "Failed to fetch profile details";
});
},
});

export const { clearGroups, _setSelectedGroup, setProfiles, updateGroupAndProfiles } = groupSlice.actions;

export const setSelectedGroup = (groupName) => (dispatch, getState) => {
  dispatch(_setSelectedGroup(groupName));
  const { groups } = getState().groups;

  // --- THIS IS THE FIX: Add a guard to prevent errors when groupName is null ---
  if (groupName) {
    const selectedGroupData = groups.find(g => g.name === groupName);
    if (selectedGroupData?._id) {
      dispatch(fetchAssets(selectedGroupData._id));
    } else {
      console.warn(`[GroupSlice] Could not find group ID for "${groupName}" to fetch assets.`);
    }
  }
};

export default groupSlice.reducer;